import { matchPath } from 'react-router-dom';

import { AppPaths } from '@/app-constants';
import { compareChatsByLastMessage } from '@/helpers/compareChatsByLastMessage';
import { ChatDto, UserMessageDto } from '@/lib/types';

/** Id of the conversation open in the inbox right now, if any. */
export function getOpenChatId(
  pathname: string = window.location.pathname,
): string | undefined {
  return matchPath(AppPaths.CHATS_SINGLE, pathname)?.params.id;
}

type ApplyNewMessageOptions = {
  ownUserId?: string;
  openChatId?: string;
};

/**
 * Applies a live `message:new` event to the cached chat list (mutates the
 * Immer draft). The open conversation marks itself read as messages arrive,
 * and own messages are never unread, so only other conversations count up.
 *
 * Returns false when the chat is not in the list, so the caller can refetch.
 */
export function applyNewMessageToChats(
  draft: ChatDto[],
  message: UserMessageDto,
  { ownUserId, openChatId }: ApplyNewMessageOptions,
): boolean {
  const chat = draft.find((item) => item.id === message.chatId);
  if (!chat) {
    return false;
  }

  // The same event can arrive twice (e.g. two tabs on one account).
  if (chat.lastMessage?.id === message.id) {
    return true;
  }

  chat.lastMessage = message;
  const isOwn = !!ownUserId && message.userId === ownUserId;
  if (!isOwn && message.chatId !== openChatId) {
    chat.unreadMessages = (chat.unreadMessages || 0) + 1;
  }
  draft.sort(compareChatsByLastMessage);

  return true;
}
